import { defineStore } from 'pinia';
import { LocalStorage } from 'quasar';
import { replacer, reviver } from 'src/utils/mapUtil';
import { getUserBasedKey } from 'src/utils/cacheHelper';
import { getSearchCapabilities } from 'src/services/searchFieldsCatalogService';

/**
 * Drop is_fulltext / is_vector from a Map when the org has no capability.
 * Bug-2026-04-29: localStorage from a previous session (or another org
 * the user was in) can carry stale `is_fulltext: "true"` flags. The
 * dashboard's WebSocket producer reads from quickFilterMap, so leaving
 * the flag in causes the backend (and the websocket consumer) to route
 * through fulltextsearch and 503 / hang for orgs that have no index.
 */
function _dropDisallowedCapabilityFlags(map: Map<string, any>): Map<string, any> {
  const caps = getSearchCapabilities();
  if (!caps.fulltext_enabled) {
    map.delete('is_fulltext');
    map.delete('is_vector');
  } else if (!caps.vector_enabled) {
    map.delete('is_vector');
  }
  return map;
}

export const useColumnFilterStore = defineStore('column_filter_store', {
  state: () => ({
    quickFilterMap: new Map() as Map<string, any>,
    tempFilterMap: new Map() as Map<string, any>,
    componentLifeCycle: new Map() as Map<string, boolean>,
  }),

  actions: {
    resetFilters(filtersKey?: string) {
      this.quickFilterMap = new Map();
      if (filtersKey) {
        this.saveFilters(filtersKey);
      }
    },

    saveFilters(filtersKey: string) {
      const str = JSON.stringify(this.quickFilterMap, replacer);
      LocalStorage.set(getUserBasedKey(filtersKey), str);
    },

    getSavedFilters(filtersKey: string) {
      try {
        const value = LocalStorage.getItem(getUserBasedKey(filtersKey)) as string;
        const newValue = JSON.parse(value, reviver);
        if (newValue) {
          // Defense in depth: a previous session may have stored
          // is_fulltext/is_vector. Drop them now if this org doesn't
          // have the capability — prevents the websocket payload from
          // re-emitting "true" for an org without a fulltext index.
          this.quickFilterMap = _dropDisallowedCapabilityFlags(newValue);
          return;
        }
      } catch (err) {}
    },

    overWriteFilters(filterMap: Map<string, any>, filtersKey: string) {
      // DRFS-583 — an explicit dashboard switch (tab/list click →
      // `selectDashboard(..., overwrite=true)`) must fully swap the
      // active filter set to the destination dashboard's saved view,
      // including `raw_query`. If the destination has no `raw_query`,
      // it is cleared. This supersedes the DOCB-11199 #2 chip-slot
      // preservation: Ruiz dashboards persist their own `raw_query`,
      // so carrying the previous one across an explicit switch made
      // the new dashboard's saved query unreachable. Initial mount
      // restore (`overwrite=false`) does not call this path, so a
      // page refresh still cannot wipe a user's in-progress chip query.
      const next = new Map(filterMap) as Map<string, any>;
      this.quickFilterMap = _dropDisallowedCapabilityFlags(next);
      this.saveFilters(filtersKey);
    },
  },
});
